import { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

/**
 * Tiny inline "Generate with AI" pill button — call beside any project metadata
 * field (research_question, title, abstract, keywords, objectives, hypothesis).
 *
 * Props:
 *   userId, projectId, field, language
 *   onResult(value) — called with the generated string; parent decides what to do
 */
export default function GenerateFieldButton({ userId, projectId, field, language = 'en', onResult, className = '' }) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      const res = await thesisApi.generateField(userId, projectId, field, language);
      if (!res?.value) {
        toast.warning('AI returned an empty result.');
        return;
      }
      onResult?.(res.value);
      toast.success(`${field.replace('_', ' ')} generated`);
    } catch (e) {
      toast.error('Generation failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold bg-[#1d4ed8]/10 hover:bg-[#1d4ed8]/20 text-[#1d4ed8] dark:text-[#7aa9ff] transition-colors disabled:opacity-50 ${className}`}
      data-testid={`generate-field-${field}`}
      title={`Generate ${field.replace('_', ' ')} with AI`}
    >
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
      Generate
    </button>
  );
}
